import React, { useEffect, useRef, useState } from 'react'
import { Save, X, Check } from 'lucide-react'
import {
    saveAiProjectDb,
    getAiProject,
    getCurrentProjectId,
} from './aiProjectDb'
import styles from './AiSaveDialog.module.css'

const MAX_IMAGES = 6

const readFileAsDataUrl = (file) =>
    new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result || ''))
        reader.onerror = () => resolve('')
        reader.readAsDataURL(file)
    })

// Save-project dialog: name, description, a video link and up to six images.
// The first image doubles as the showcase thumbnail. Saving writes everything
// (plus the full project payload) to the local project library in IndexedDB.
const AiSaveDialog = ({ onClose, onSaved }) => {
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [videoLink, setVideoLink] = useState('')
    const [images, setImages] = useState([])
    const [saving, setSaving] = useState(false)
    const [saved, setSaved] = useState(false)
    const [error, setError] = useState('')
    const slotRef = useRef(null)
    const pickIndex = useRef(0)

    // Prefill from the project currently being edited, so Save updates it
    // instead of asking everything again.
    useEffect(() => {
        let active = true
        const id = getCurrentProjectId()
        if (!id) return
        getAiProject(id)
            .then((rec) => {
                if (!active || !rec) return
                setName(rec.name || '')
                setDescription(rec.description || '')
                setVideoLink(rec.videoLink || '')
                setImages(rec.images || [])
            })
            .catch(() => {})
        return () => {
            active = false
        }
    }, [])

    const openPicker = (i) => {
        pickIndex.current = i
        slotRef.current && slotRef.current.click()
    }

    const onFile = async (e) => {
        const file = e.target.files && e.target.files[0]
        e.target.value = ''
        if (!file) return
        const url = await readFileAsDataUrl(file)
        if (!url) return
        setImages((prev) => {
            const next = prev.slice()
            next[pickIndex.current] = url
            return next.filter(Boolean).slice(0, MAX_IMAGES)
        })
    }

    const removeImage = (i) => {
        setImages((prev) => prev.filter((_, idx) => idx !== i))
    }

    const onSave = async () => {
        if (saving) return
        if (!name.trim()) {
            setError('Please give your project a name.')
            return
        }
        setError('')
        setSaving(true)
        try {
            const rec = await saveAiProjectDb({
                name: name.trim(),
                description,
                videoLink,
                images,
            })
            onSaved && onSaved(rec)
            // Show a clear "Project saved" confirmation, then close.
            setSaved(true)
            setTimeout(() => onClose && onClose(), 900)
        } catch (e) {
            setError('Could not save. Your browser storage may be full.')
            setSaving(false)
        }
    }

    // Always render six slots; filled ones show the image, empty ones a "+".
    const slots = Array.from({ length: MAX_IMAGES }, (_, i) => images[i] || null)

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
                <button
                    type="button"
                    className={styles.close}
                    onClick={onClose}
                    aria-label="Close"
                >
                    <X size={22} />
                </button>

                <div className={styles.body}>
                    <div className={styles.formCol}>
                        <input
                            className={styles.nameInput}
                            placeholder="Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            maxLength={80}
                        />
                        <textarea
                            className={styles.descInput}
                            placeholder="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            maxLength={1000}
                        />
                        <input
                            className={styles.linkInput}
                            placeholder="Video Link"
                            value={videoLink}
                            onChange={(e) => setVideoLink(e.target.value)}
                        />
                    </div>

                    <div className={styles.imagesCol}>
                        <p className={styles.imagesTitle}>Add Images</p>
                        <div className={styles.imagesGrid}>
                            {slots.map((img, i) => (
                                <div key={i} className={styles.slot}>
                                    {img ? (
                                        <>
                                            <img
                                                src={img}
                                                alt={`Project ${i + 1}`}
                                                className={styles.slotImg}
                                            />
                                            <button
                                                type="button"
                                                className={styles.slotRemove}
                                                onClick={() => removeImage(i)}
                                                aria-label="Remove image"
                                            >
                                                <X size={14} />
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            type="button"
                                            className={styles.slotAdd}
                                            onClick={() => openPicker(i)}
                                            aria-label="Add image"
                                        >
                                            +
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {error && <p className={styles.error}>{error}</p>}
                {saved && <p className={styles.savedMsg}>Project saved ✓</p>}

                <button
                    type="button"
                    className={styles.saveBtn}
                    onClick={onSave}
                    disabled={saving || saved}
                    aria-label="Save project"
                >
                    {saved ? <Check size={26} /> : <Save size={26} />}
                </button>

                <input
                    ref={slotRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={onFile}
                />
            </div>
        </div>
    )
}

export default AiSaveDialog
